import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, uploadFile, fileUrl, formatTanggal, BULAN } from "@/lib/api";
import { FilterBar } from "@/components/FilterBar";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU } from "@/lib/nav";
import { toast } from "sonner";
import { Plus, X, Upload, FileText } from "lucide-react";

const JENIS_LABEL = {
  terlambat: "Terlambat", alfa: "Izin", sakit: "Sakit",
  dinas_luar: "Dinas Luar", pulang_cepat: "Pulang Cepat",
};

    const IZIN_F_EMPTY = { jenis_izin: "", bulan: "", tahun: "" };
    const IZIN_JENIS_OPTS = [
      { v: "terlambat", label: "Terlambat" },
      { v: "alfa", label: "Izin" },
      { v: "sakit", label: "Sakit" },
      { v: "dinas_luar", label: "Dinas Luar" },
      { v: "pulang_cepat", label: "Pulang Cepat" },
    ];
    const IZIN_TAHUN_OPTS = (() => {
      const y = new Date().getFullYear();
      return [y + 1, y, y - 1, y - 2, y - 3].map((n) => ({ v: String(n), label: String(n) }));
    })();
const KEPADA_OPTS = ["Kepala Sekolah", "Ketua Yayasan"];
const MELALUI_OPTS = ["Lisan", "Pesan", "Telepon"];
const buildEmptyForm = () => ({ jenis_izin: "sakit", alasan: "", tanggal: new Date().toISOString().slice(0, 10), bukti_file_id: null, kepada: "", melalui: "" });

export function IzinGuru() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(buildEmptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState(IZIN_F_EMPTY);
  const [applied, setApplied] = useState(IZIN_F_EMPTY);

  const load = useCallback(async () => { const { data } = await api.get("/izin-guru"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const r = await uploadFile(f); setForm((s) => ({ ...s, bukti_file_id: r.id })); toast.success("File diunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.jenis_izin === "pulang_cepat" && (!form.kepada || !form.melalui)) {
      return toast.error("Untuk Pulang Cepat, pilih 'Izin Kepada' dan 'Melalui' terlebih dahulu");
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Only send kepada/melalui when jenis_izin is pulang_cepat
      if (payload.jenis_izin !== "pulang_cepat") { payload.kepada = null; payload.melalui = null; }
      await api.post("/izin-guru", payload);
      toast.success("Izin terkirim & notifikasi email dikirim");
      setShow(false); setForm(buildEmptyForm()); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const visibleIzin = items.filter((it) => {
        if (applied.jenis_izin && it.jenis_izin !== applied.jenis_izin) return false;
        if (applied.bulan && (it.tanggal || "").slice(5, 7) !== applied.bulan) return false;
        if (applied.tahun && (it.tanggal || "").slice(0, 4) !== String(applied.tahun)) return false;
        return true;
      });

      return (
        <Layout nav={NAV_GURU}>
      <PageTitle title="Izin Guru" action={<button onClick={() => { setForm(buildEmptyForm()); setShow(true); }} className="pp-btn-primary" data-testid="izin-open"><Plus className="w-4 h-4" />Input Izin</button>} />
      {show && (
        <section className="pp-card p-6 mb-6 animate-fade-up">
          <div className="flex items-center justify-between mb-5"><h3 className="text-lg font-semibold">Input Izin</h3><button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="izin-form">
            <div><label className="pp-label">Jenis Izin *</label>
              <select required className="pp-input" value={form.jenis_izin} onChange={(e) => setForm({ ...form, jenis_izin: e.target.value })} data-testid="izin-jenis">
                <option value="terlambat">Terlambat</option>
                <option value="alfa">Izin</option>
                <option value="sakit">Sakit</option>
                <option value="dinas_luar">Dinas Luar</option>
                <option value="pulang_cepat">Pulang Cepat</option>
              </select>
            </div>
            <div><label className="pp-label">Tanggal *</label>
              <input
                type="date"
                readOnly
                disabled
                className="pp-input bg-slate-100 text-slate-600 cursor-not-allowed"
                value={form.tanggal}
                data-testid="izin-tanggal"
              />
              <p className="text-[11px] text-slate-500 mt-1">Otomatis terkunci ke tanggal hari ini.</p>
            </div>
            {form.jenis_izin === "pulang_cepat" && (
              <>
                <div><label className="pp-label">Izin Kepada *</label>
                  <select required className="pp-input" value={form.kepada} onChange={(e) => setForm({ ...form, kepada: e.target.value })} data-testid="izin-kepada">
                    <option value="">— Pilih —</option>
                    {KEPADA_OPTS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div><label className="pp-label">Melalui *</label>
                  <select required className="pp-input" value={form.melalui} onChange={(e) => setForm({ ...form, melalui: e.target.value })} data-testid="izin-melalui">
                    <option value="">— Pilih —</option>
                    {MELALUI_OPTS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </>
            )}
            <div className="sm:col-span-2"><label className="pp-label">Alasan</label>
              <textarea rows={2} className="pp-input" value={form.alasan} onChange={(e) => setForm({ ...form, alasan: e.target.value })} data-testid="izin-alasan" />
            </div>
            <div className="sm:col-span-2"><label className="pp-label">Bukti (Foto/PDF)</label>
              <label className="pp-btn-secondary cursor-pointer"><Upload className="w-4 h-4" />{uploading ? "Mengunggah…" : (form.bukti_file_id ? "Ganti" : "Pilih File")}
                <input type="file" accept="image/*,.pdf" onChange={upload} className="hidden" data-testid="izin-bukti" />
              </label>
              {form.bukti_file_id && <span className="ml-3 text-sm text-emerald-600">✓ Siap</span>}
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShow(false)} className="pp-btn-secondary">Batal</button>
              <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="izin-submit">{saving ? "Menyimpan…" : "Simpan Izin"}</button>
            </div>
          </form>
        </section>
      )}
      <FilterBar
            filters={filters} setFilters={setFilters}
            onApply={() => setApplied({ ...filters })}
            onReset={() => { setFilters(IZIN_F_EMPTY); setApplied(IZIN_F_EMPTY); }}
            dataTestidPrefix="guru-izin-filter"
            fields={[
              { key: "jenis_izin", type: "select", label: "Jenis Izin", options: IZIN_JENIS_OPTS },
              { key: "bulan", type: "select", label: "Bulan", options: BULAN },
              { key: "tahun", type: "select", label: "Tahun", options: IZIN_TAHUN_OPTS },
            ]}
          />
          <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full text-sm" data-testid="izin-table">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Tanggal</th>
              <th className="text-left px-5 py-3">Jenis</th>
              <th className="text-left px-5 py-3">Kepada</th>
              <th className="text-left px-5 py-3">Melalui</th>
              <th className="text-left px-5 py-3">Alasan</th>
              <th className="text-left px-5 py-3">Bukti</th>
            </tr></thead>
            <tbody>
              {visibleIzin.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada izin.</td></tr>}
              {visibleIzin.map((it) => (
                <tr key={it.id} className="pp-row" data-testid={`izin-row-${it.id}`}>
                  <td className="px-5 py-3 font-mono text-xs">{formatTanggal(it.tanggal)}</td>
                  <td className="px-5 py-3"><span className="pp-badge-info">{JENIS_LABEL[it.jenis_izin] || it.jenis_izin}</span></td>
                  <td className="px-5 py-3 text-sm">{it.jenis_izin === "pulang_cepat" ? (it.kepada || "—") : "—"}</td>
                  <td className="px-5 py-3 text-sm">{it.jenis_izin === "pulang_cepat" ? (it.melalui || "—") : "—"}</td>
                  <td className="px-5 py-3 text-sm">{it.alasan || "—"}</td>
                  <td className="px-5 py-3">{it.bukti_file_id ? <a href={fileUrl(it.bukti_file_id)} target="_blank" rel="noreferrer" className="text-indigo-600 text-xs inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Lihat</a> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}
